/**
 * Toast notification system
 * Provides toast context, provider, and useToast hook
 */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react'
import { CheckCircle2, XCircle, AlertCircle, Flame, X } from 'lucide-react'

// ─── Types ────────────────────────────────────────────────────────────────────

type ToastType = 'success' | 'error' | 'info' | 'streak'

interface Toast {
  id: string
  type: ToastType
  message: string
  description?: string
}

interface ToastContextValue {
  toasts: Toast[]
  addToast: (type: ToastType, message: string, description?: string) => void
  removeToast: (id: string) => void
  success: (message: string, description?: string) => void
  error: (message: string, description?: string) => void
  info: (message: string, description?: string) => void
  streak: (message: string, description?: string) => void
}

// ─── Context ──────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | null>(null)

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const addToast = useCallback((type: ToastType, message: string, description?: string) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`
    setToasts((prev) => {
      const withoutDuplicate = prev.filter(
        (t) => !(t.type === type && t.message === message && t.description === description),
      )
      return [...withoutDuplicate, { id, type, message, description }]
    })

    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 5000)
  }, [])

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const success = useCallback((message: string, description?: string) => {
    addToast('success', message, description)
  }, [addToast])

  const error = useCallback((message: string, description?: string) => {
    addToast('error', message, description)
  }, [addToast])

  const info = useCallback((message: string, description?: string) => {
    addToast('info', message, description)
  }, [addToast])

  const streak = useCallback((message: string, description?: string) => {
    addToast('streak', message, description)
  }, [addToast])

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, info, streak }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

// ─── Toast Container ──────────────────────────────────────────────────────────

function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  if (toasts.length === 0) return null

  return (
    <div className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] flex flex-col items-center gap-2 w-[calc(100%-2rem)] max-w-md pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  )
}

// ─── Toast Item ───────────────────────────────────────────────────────────────

function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const config = {
    success: {
      icon: CheckCircle2,
      bg: 'bg-[hsl(142,35%,11%)] border-green-500/40',
      iconColor: 'text-green-500',
    },
    error: {
      icon: XCircle,
      bg: 'bg-[hsl(0,40%,12%)] border-red-500/50',
      iconColor: 'text-red-500',
    },
    info: {
      icon: AlertCircle,
      bg: 'bg-[hsl(217,40%,12%)] border-blue-500/40',
      iconColor: 'text-blue-500',
    },
    streak: {
      icon: Flame,
      bg: 'bg-[hsl(25,45%,12%)] border-orange-500/40',
      iconColor: 'text-orange-500',
    },
  }

  const { icon: Icon, bg, iconColor } = config[toast.type]

  return (
    <div
      className={`${bg} w-full border backdrop-blur-md rounded-xl p-4 shadow-2xl animate-slide-in flex items-start gap-3 pointer-events-auto`}
      role="alert"
    >
      <Icon className={`w-5 h-5 ${iconColor} flex-shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{toast.message}</p>
        {toast.description && (
          <p className="text-xs text-muted-foreground mt-0.5">{toast.description}</p>
        )}
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}
